'use client';

import { useEffect, useRef, useState } from 'react';

/** Scene units → percentage of the wall layer (shared by the scene layers). */
export const pct = (v, total) => `${(v / total) * 100}%`;

/**
 * A symbol-library SVG used as a spot icon in a scene: fetched through the
 * injected image client, recolored to one ink, guide/rule paths hidden.
 * Positioned in viewBox percentages by the parent.
 */
export default function SceneIcon({ icon, viewBox, imageClient, delay = 0 }) {
  const hostRef = useRef(null);
  const [svg, setSvg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!imageClient) return undefined;
    imageClient.fetchSymbolImage(icon.symbolId).then((info) => {
      if (!cancelled) setSvg(info?.svg || null);
    });
    return () => {
      cancelled = true;
    };
  }, [icon.symbolId, imageClient]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (!svg) {
      host.innerHTML = ''; // symbol changed or fetch came back empty: drop the stale art
      return;
    }
    host.innerHTML = svg;
    const el = host.querySelector('svg');
    if (!el) return;
    el.setAttribute('width', '100%');
    el.setAttribute('height', '100%');
    el.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    el.querySelectorAll('style').forEach((s) => s.remove());
    el.querySelectorAll('path, line, polyline, polygon, rect, circle, ellipse').forEach((n) => {
      if (n.id && /^(rulepath|textpath)/i.test(n.id)) {
        n.style.visibility = 'hidden';
        return;
      }
      if (n.getAttribute('stroke') !== 'none') n.style.setProperty('stroke', icon.color, 'important');
      const f = n.getAttribute('fill');
      if (f && f !== 'none') n.style.setProperty('fill', icon.color, 'important');
    });
  }, [svg, icon.color]);

  return (
    <div
      ref={hostRef}
      data-sid={icon.id}
      className="scene-icon scene-icon-in"
      style={{
        position: 'absolute',
        left: pct(icon.x, viewBox.width),
        top: pct(icon.y, viewBox.height),
        width: pct(icon.w, viewBox.width),
        height: pct(icon.h, viewBox.height),
        pointerEvents: 'none',
        animationDelay: delay ? `${delay}ms` : undefined,
      }}
    />
  );
}
